import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { Timer } from "easytimer.js";
import { NGXLogger } from "ngx-logger";
import { interval, of, Subscription } from "rxjs";
import { delay, filter, switchMap, withLatestFrom, distinctUntilChanged } from "rxjs/operators";
import { SignInState } from "src/app/components/allusers/signin/signin.model";
import { getSignedInUser } from "src/app/components/allusers/signin/signin.selector";
import { ChapterSection } from "src/app/models/chapter-sections.model";
import { Chapter } from "src/app/models/chapters.model";
import { Course } from "src/app/models/course.model";
import { Progress } from "src/app/models/progress.model";
import { Quiz } from "src/app/models/quiz.model";
import { User } from "src/app/models/user.model";
import { CertificatesService } from "src/app/services/certificates.service";
import { ChapterSectionsService } from "src/app/services/chapter-sections.service";
import { ChaptersService } from "src/app/services/chapters.service";
import { EssayAnswersService } from "src/app/services/essay-answers.service";
import { MemberlocService } from "src/app/services/memberloc.service";
import { ProgressService } from "src/app/services/progress.service";
import { QuestionsService } from "src/app/services/questions.service";
import { QuizesService } from "src/app/services/quizes.service";
import { CourseTimerState } from "../course-timer/course-timer.model";
import { isTimerIncomplete, isTimerStart, isTimerStopped } from "../course-timer/course-timer.selector";
import { CourseState } from "../courses/courses.reducer";
import { getCourseSelected, getSelectedCourseState, isCourseNotSelected } from "../courses/courses.selector";
import { QuizTestsComponent } from "../quiz-tests/quiz-tests.component";
import {
  ChapterShowAction,
  ChapterViewActionTypes,
  ChapterFinalShowAction,
  ChapterQuizzesIncompleteAction,
  ChapterUpdateAction
} from "./chapter-view.actions";
import { ChapterViewState } from "./chapter-view.model";
import {
  getCurrentChapterViewState,
  isChapterShow,
  isFinalShow,
  isQuizzesIncomplete,
  isUnknownChapterShow
} from "./chapter-view.selector";
import { QuizTestsState } from "src/app/components/members/quiz-tests/quiz-tests.model";
import { InitializationAction, QuizTestsActionTypes } from "src/app/components/members/quiz-tests/quiz-tests.actions";

interface ChapterQuizzes {
  [key: string]: Quiz[];
}


@Component({
  selector: "app-chapter-view",
  templateUrl: "./chapter-view.component.html",
  styleUrls: ["./chapter-view.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class ChapterViewComponent implements OnInit, OnDestroy {
  @ViewChild("chapters") chaptersComponent: QuizTestsComponent;
  @ViewChild("chapters") quizElement: ElementRef;
  @ViewChild("content") eChapterContent: ElementRef;
  @ViewChild("courseViewContent") courseViewContent: ElementRef;
  
  courseChapters: Chapter[];
  courseQuizzes: Quiz[];
  links = [];
  activeLink;
  isChapterDropdownOpen = false;
  isChapterSectionOpen = false;

  /** Touch coordinates for swipe detection (mobile section navigation) */
  private touchStartX: number;
  private touchStartY: number;
  private readonly swipeThresholdPx = 50;
  private readonly maxVerticalSwipePx = 80;

  get hasSelectedSection(): boolean {
    return !!this.currChapterViewState?.sectionId;
  }

  isSectionSelected(section: ChapterSection): boolean {
    const sectionId = this.currChapterViewState?.sectionId;
    const quizId = this.currChapterViewState?.quizId;
    const chapterId = this.currChapterViewState?.chapterId;
    
    // Check if section is selected by sectionId
    if (sectionId && section.id == parseInt(sectionId)) {
      return true;
    }
    
    // Check if section links to the current quiz
    if (quizId && chapterId && this.currentCourse) {
      // Check if section.location contains the quiz link
      if (section.location) {
        const quizLinkPattern = `/quizzes/${quizId}`;
        if (section.location.includes(quizLinkPattern)) {
          return true;
        }
      }
      // Check if section.content contains a link to the quiz
      // This would be in the HTML content, so we check if the section's chapter matches
      // and if there's a quiz link pattern in the location
      if (section.content && section.chapterId == parseInt(chapterId)) {
        // The content might have embedded quiz links, but we'll rely on location for now
        // If location has the quiz link, it's selected
      }
    }
    
    return false;
  }

  isUnknownChapterShow$ = this.chapterViewStore.pipe(
    select(isUnknownChapterShow),
    filter((val) => (val ? true : false))
  );

  isChapterShow$ = this.chapterViewStore.pipe(
    select(isChapterShow),
    filter((val) => (val ? true : false))
  );

  isFinalShow$ = this.chapterViewStore.pipe(
    select(isFinalShow),
    filter((val) => (val ? true : false))
  );

  isQuizzesIncomplete$ = this.chapterViewStore.pipe(
    select(isQuizzesIncomplete),
    filter((val) => (val ? true : false))
  );

  currChapterViewState$ = this.chapterViewStore.pipe(
    select(getCurrentChapterViewState),    
  );

  currChapterViewState: ChapterViewState;

  subscriptions$: Array<Subscription> = [];

  chapterContent;
  sectionContent;
  chapterSections: Array<ChapterSection> = [];
  allChapters: Array<Chapter> = [];
  quizId: string = null;
  videoId: string = null;
  soundId: string = null;
  chapterId: string;
  currentUser: User;
  incompleteQuizzes = [];
  progresses: Array<Progress> = [];
  /** True once `progresses` holds a real fetch, so children can tell it apart from the initial []. */
  progressesLoaded = false;
  chapterQuizzes: ChapterQuizzes = {};
  timer: Timer;
  timerString: string;

  activatedRoute$ = this.activatedRoute.params.pipe(
    switchMap(params => {
      return of(params).pipe(
        withLatestFrom(this.courseStore.pipe(select(getSelectedCourseState))),
        switchMap(([p, course]) => {
          if (!course) {
            return of(p).pipe(delay(1000));
          }
          return of(p);
        })
      );
    }),
    withLatestFrom(this.courseStore.pipe(select(getSelectedCourseState))), // Get selected course
    filter(([params, course]) => course !== null && course !== undefined) // Ensure course exists
  );

  courseSelected$ = this.courseStore.pipe(select(getSelectedCourseState), filter(val => val !== null && val !== undefined));
  courseNotSelected$ = this.courseStore.pipe(select(isCourseNotSelected), filter(val => val))
  currentCourse: CourseState;

  courseTimerError$ = this.courseTimerStore.pipe(select(isTimerIncomplete), filter(val => val !== false));
  courseTimerStarted$ = this.courseTimerStore.pipe(select(isTimerStart), filter(val => val !== false));
  courseTimerStopped$ = this.courseTimerStore.pipe(select(isTimerStopped), filter(val => val !== false));

  constructor(
    private chaptersService: ChaptersService,
    private chapterSectionsService: ChapterSectionsService,
    private quizesService: QuizesService,
    private essayAnswersService: EssayAnswersService,
    private progressService: ProgressService,
    private cirtificatesService: CertificatesService,
    public router: Router,
    private questionsService: QuestionsService,
    private memberLocService: MemberlocService,
    private sanitizer: DomSanitizer,
    private logger: NGXLogger,
    private activatedRoute: ActivatedRoute,
    private signInStore: Store<SignInState>,
    private chapterViewStore: Store<ChapterViewState>,
    private courseStore: Store<CourseState>,
    private courseTimerStore: Store<CourseTimerState>,
    private quizTestsStore: Store<QuizTestsState>
  ) {
    this.subscriptions$.push(this.signInStore
      .pipe(select(getSignedInUser))
      .subscribe((user) => (this.currentUser = user)));

    this.subscriptions$.push(this.currChapterViewState$.subscribe(
      (state) => {
        const previousSectionId = this.currChapterViewState?.sectionId;
        this.currChapterViewState = state;
        // Close section drawer when a new section is selected (but not if user manually opened it)
        if (state?.sectionId && state.sectionId !== previousSectionId) {
          this.isChapterSectionOpen = false;
        }
      }
    ));
  }

  async ngOnInit() {    
    this.subscriptions$.push(this.activatedRoute$.subscribe(async ([params, _]) => {
      this.currentCourse = _;
      if (this.allChapters.length == 0) {
        this.allChapters = await this.getChapters();
        await this.prepareLinks();
      }
      await this.getProgress();      
      this.chapterViewStore.dispatch(
        new ChapterUpdateAction(
          {
            ...this.currChapterViewState,
            chapterId: params.chapterId,
            quizId: params.quizId,
            videoId: params.videoId,
            sectionId: params.sectionId,
            incompleteQuizzes: await this.getPrevIncompleteQuizzes(
              params.chapterId
            ),
            currentState: ChapterViewActionTypes.ChapterUpdate,
          } as ChapterViewState)
      );
    }));


    this.subscriptions$.push(
      this.courseTimerError$.subscribe((val) => {
        this.chapterViewStore.dispatch(
          new ChapterUpdateAction({ ...this.currChapterViewState, timerError: true} as ChapterViewState));
      }));

    this.subscriptions$.push(
      this.courseTimerStarted$.subscribe((val) => {
        this.chapterViewStore.dispatch(
          new ChapterUpdateAction({ ...this.currChapterViewState, timerError: false} as ChapterViewState));
      }));

    this.subscriptions$.push(
      this.courseTimerStopped$.subscribe((val) => {
        this.chapterViewStore.dispatch(
          new ChapterUpdateAction({ ...this.currChapterViewState, timerError: false} as ChapterViewState));
      }));

    this.subscriptions$.push(
      this.isUnknownChapterShow$.subscribe(() => {
        this.logger.debug(`Unknown chapter show`);
        if (this.currentCourse) {
          this.navigateToLastWorkedOn();
        }
      })
    );

    this.subscriptions$.push(
      this.isChapterShow$.subscribe(async () => {
        this.chapterViewStore.dispatch(
          new ChapterShowAction(
            {
              ...this.currChapterViewState,
              incompleteQuizzes: await this.getPrevIncompleteQuizzes(
                this.currChapterViewState.chapterId
              ),
              currentState: ChapterViewActionTypes.ChapterShow,
            } as ChapterViewState
          )
        );

        await this.getChapterQuizzes(this.currChapterViewState.chapterId);
        await this.showChapter();
        this.focusContent();
      })
    );

    this.subscriptions$.push(
      this.isFinalShow$.subscribe(async () => {
        this.chapterViewStore.dispatch(
          new ChapterFinalShowAction(
            {
              ...this.currChapterViewState,
              currentState: ChapterViewActionTypes.ChapterFinalShow,
            } as ChapterViewState
          )
        );
        this.showFinal();
      })
    );

    this.subscriptions$.push(
      this.isQuizzesIncomplete$.subscribe(async () => {
        this.incompleteQuizzes = await this.getPrevIncompleteQuizzes(
          this.currChapterViewState.chapterId
        );
        this.chapterViewStore.dispatch(
          new ChapterQuizzesIncompleteAction(
            {
              ...this.currChapterViewState,
              incompleteQuizzes: this.incompleteQuizzes,
              currentState: ChapterViewActionTypes.ChapterQuizzesIncomplete,
            } as ChapterViewState
          )
        );
        this.showIncompleteQuizzes();
      })
    );

    this.subscriptions$.push(
    this.courseNotSelected$.subscribe(val => this.currentCourse = undefined));    
  }

  ngOnDestroy() {
    if (this.subscriptions$ && this.subscriptions$.length > 0) {
      this.subscriptions$.forEach((item) => item.unsubscribe());
      this.subscriptions$ = [];
    }
  }

  async prepareLinks() {
    this.links = (await this.getChapters()).map(c => ({ label: c.ChapterName, path: "/courses/" + this.currentCourse.course.id + "/chapters/" + c.ID }));
    const final = this.links.find(l => l.label.trim().toUpperCase().indexOf("FINAL") != -1);
    final.label = "Take My Final Now";
    final.path = "/courses/" + this.currentCourse.course.id + "/chapters/final";
    this.activeLink = this.links[0];
  }

  async navigateToLastWorkedOn() {
    this.logger.info(`Navigating to last worked on - ${this.currentCourse.course.id} - ${this.currentUser.memberid}`);
    const memberloc = await this.memberLocService.getMemberLocationByCourse(
      this.currentCourse.course.id,
      this.currentUser.memberid
    );

    if (
      memberloc &&
      memberloc.section &&
      memberloc.quid &&
      memberloc.quid !== "final" &&
      memberloc.section !== "final"
    ) {
      this.router.navigate(["courses", this.currentCourse.course.id,
        "chapters",
        parseInt(memberloc.section),
        "quizzes",
        memberloc.quid,
      ]);
    } else if (
      memberloc &&
      memberloc.section &&
      memberloc.quid &&
      memberloc.quid === "final" &&
      memberloc.section === "final"
    ) {
      this.router.navigate(["courses", this.currentCourse.course.id, "chapters", "final"]);
    } else {
      const firstChapterId = (await this.getChapters())[0].ID;
      this.router.navigate(["courses", this.currentCourse.course.id, "chapters", firstChapterId]);
    }
  }

  async getProgress() {
    const progresses = await this.progressService.getProgressByCourse(
      this.currentCourse.course.id,
      this.currentUser.memberid
    );

    this.progresses = progresses.sort(
      (a, b) => new Date(b.DTStamp).getTime() - new Date(a.DTStamp).getTime()
    );
    this.progressesLoaded = true;
  }

  async getChapters() {
    if (!this.courseChapters) {
      this.courseChapters = await this.chaptersService.getChaptersBy(this.currentCourse.course.id);
      return this.courseChapters;
    }
    return this.courseChapters;
  }

  async showIncompleteQuizzes() {
    let content = `<div class="row media">
      <div class="fx-layout-column">
        <div class="row">
          <h5>Notice - Please complete previous chapter before progressing to the next one</h5>
          <div>`;

    for (const quiz of this.incompleteQuizzes) {
      content +=
        `<p class="red-text">Quiz Not Complete: <a href="` +
        this.getIncompleteLink(quiz) +
        `">` +
        quiz.QuizLocation +
        ` - quiz
        ` +
        quiz.QuizID +
        ` - ` +
        quiz.QuizTitle +
        `</a></p>`;
    }

    content += `</div>
        </div>
      </div>
    </div>`;
    this.chapterContent = this.sanitizer.bypassSecurityTrustHtml(content);
  }

  async showChapter() {
    if (this.currChapterViewState.chapterId == "final") {
      this.logger.info(`New Showing final...`);
      return;
    }

    this.chapterSections = await this.chapterSectionsService.findAll(this.currentCourse.course.id, parseInt(this.currChapterViewState.chapterId));

    this.showSection();

    // parentId can be a number, null, or a string ("0" for bigint columns), so
    // compare numerically - otherwise headers with parentId "0" are missed and
    // the chapter renders no sections.
    const sectionHeaders = this.chapterSections.filter((s) => !Number(s.parentId)).sort((a, b) => a.order - b.order);
    this.chapterSections = sectionHeaders.flatMap((s) => {
      const children = this.chapterSections.filter((c) => Number(c.parentId) === Number(s.id)).sort((a, b) => a.order - b.order);
      return [s, ...children]
    });
  }

  async showFinal() {
    this.logger.info(`Showing final...`);
    this.sectionContent = null;
  }

  async getChapterQuizzes(chapterId) {
    if (this.chapterQuizzes && this.chapterQuizzes[chapterId]) {
      return this.chapterQuizzes[chapterId];
    } else {
      this.courseQuizzes = await this.quizesService.findAllPerCourse(this.currentCourse.course.id);
      this.courseQuizzes.forEach((quiz) => {
        this.chapterQuizzes[quiz.QuizLocation2] = this.chapterQuizzes[quiz.QuizLocation2] || [];
        this.chapterQuizzes[quiz.QuizLocation2].push(quiz);
      });
      return this.chapterQuizzes[chapterId];
    }
  }

  async getPrevIncompleteQuizzes(chapterId) {
    if (this.currentUser.memberstatus === "admin") {
      return [];
    }

    if (chapterId) {
      let chapters = this.getPrevChapters(chapterId);

      this.logger.info(`Checking prev chapters - ${JSON.stringify(chapters)}`);

      for (const chapter of chapters) {
        const incompleteQuizzes = await this.getIncompleteQuizzesFor(chapter.ID);
        if (incompleteQuizzes.length > 0) {
          return incompleteQuizzes;
        }
      }
      return [];
    }

    this.logger.info(
      `No chapter id found. Skipping prev chapter completion check...`
    );
    return [];
  }

  getPrevChapters(chapterId) {
    if (!chapterId) {
      chapterId = this.currChapterViewState.chapterId;
    }

    let chapters = [];
    if (Number.parseInt(chapterId)) {
      this.logger.info(
        `Checking prev chapters completed for chapter - ${chapterId} - ${this.currentUser.memberid} - all chapter - ${this.allChapters.length}`
      );
      chapters = this.allChapters.filter(
        (c) => c.ID < parseInt(chapterId, null)
      );
    } else if (chapterId == "final") {
      chapters = this.allChapters.filter((c) => c.ChapterName.toUpperCase() !== 'FINAL');
    }

    return chapters;
  }

  async getIncompleteQuizzesFor(chapterId): Promise<Quiz[]> {
    if (!chapterId) {
      return [];
    }

    let quizzes = await this.getChapterQuizzes(chapterId);
    const incompleteQuizzes = [];
    for (const quiz of quizzes) {
      if (
        !this.progresses.find((p) => p.QuizID == quiz.QuizID && Number(p.Score) >= 60)
      ) {
        this.logger.info(
          `Failed prev chapter check for chapter - ${chapterId} - quiz - ${JSON.stringify(
            quiz.QuizID
          )}`
        );
        incompleteQuizzes.push(quiz);
      }
    }
    return incompleteQuizzes;
  }

  focusContent() {
    const chapterId = this.activatedRoute.snapshot.paramMap.get("chapterId");
    const quizId = this.activatedRoute.snapshot.paramMap.get("quizId");
    const sectionId = this.activatedRoute.snapshot.paramMap.get("sectionId");
    const assetId = this.activatedRoute.snapshot.paramMap.get("assetId");

    if (quizId) {
      setTimeout(() => {
        const element = this.eChapterContent.nativeElement.querySelector(
          "a[href='courses/" + this.currentCourse.course.id + "/chapters/" + chapterId + "/quizzes/" + quizId + "']"
        );
        if (element) {
          element.focus();
        }
      }, 500);
    }
    if (assetId) {
      setTimeout(() => {
        const element = this.eChapterContent.nativeElement.querySelector(
          "a[href='courses/" + this.currentCourse.course.id + "/chapters/" + chapterId + "/assets/" + assetId + "']"
        );

        if (element) {
          element.focus();
        }
      }, 500);
    }
    if (sectionId) {
      setTimeout(() => {
        const element = this.eChapterContent.nativeElement.querySelector(
          "a[href='courses/" + this.currentCourse.course.id + "/chapters/" + chapterId + "/sections/" + sectionId + "']"
        );

        if (element) {
          element.focus();
        }
      }, 500);
    }
  }

  getIncompleteLink(quiz) {
    return "courses/" + this.currentCourse.course.id + "/chapters/" + quiz.QuizLocation2 + "/quizzes/" + quiz.QuizID;
  }

  getSectionLink(section: ChapterSection) {
    return "courses/" + this.currentCourse.course.id + "/chapters/" + section.chapterId + "/sections/" + section.id;
  }

  showSection() {
    if (this.currChapterViewState.sectionId) {
      const section = this.chapterSections.find(s => s.id == parseInt(this.currChapterViewState.sectionId))
      if (section && section.content) {
        this.sectionContent = this.sanitizer.bypassSecurityTrustHtml(section.content);
        return;
      }
    }

    this.sectionContent = null;
  }

  toggleChapterDropdown() {
    this.isChapterDropdownOpen = !this.isChapterDropdownOpen;
  }

  closeChapterDropdown() {
    this.isChapterDropdownOpen = false;
  }

  toggleChapterSection() {
    this.isChapterSectionOpen = !this.isChapterSectionOpen;
  }

  closeChapterSectionOnMobile() {
    // Only close on mobile (when window width is less than 768px)
    if (window.innerWidth < 768) {
      this.isChapterSectionOpen = false;
      this.isChapterDropdownOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    // Close chapter-content on mobile when clicking outside
    if (window.innerWidth < 768 && this.isChapterSectionOpen) {
      // Check if click is outside the chapter-content and hamburger button
      const hamburgerButton = (event.target as HTMLElement)?.closest('button[aria-label="Toggle chapter sections"]');
      const chapterContent = this.eChapterContent?.nativeElement;
      
      if (chapterContent && !chapterContent.contains(event.target as Node) && !hamburgerButton) {
        this.isChapterSectionOpen = false;
      }
    }
  }

  getActiveChapterLabel(): string {
    if (!this.links || this.links.length === 0) {
      return 'Select Chapter';
    }
    const currentUrl = this.router.url;
    const activeLink = this.links.find(link => {
      return currentUrl === link.path || currentUrl.startsWith(link.path + '/');
    });
    return activeLink ? activeLink.label : (this.links[0]?.label || 'Select Chapter');
  }

  onTouchStart(event: TouchEvent) {
    if (window.innerWidth >= 768) return;
    if (event.changedTouches?.length) {
      this.touchStartX = event.changedTouches[0].clientX;
      this.touchStartY = event.changedTouches[0].clientY;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (window.innerWidth >= 768) return;
    if (!event.changedTouches?.length || this.touchStartX == null) return;
    const endX = event.changedTouches[0].clientX;
    const endY = event.changedTouches[0].clientY;
    const deltaX = endX - this.touchStartX;
    const deltaY = Math.abs(endY - this.touchStartY);
    this.touchStartX = null;
    this.touchStartY = null;
    if (deltaY > this.maxVerticalSwipePx) return;
    if (deltaX > this.swipeThresholdPx) {
      this.goToPreviousSection();
    } else if (deltaX < -this.swipeThresholdPx) {
      this.goToNextSection();
    }
  }

  private getCurrentNavigableIndex(navigable: ChapterSection[]): number {
    const sectionId = this.currChapterViewState?.sectionId;
    if (sectionId) {
      const byId = navigable.findIndex(s => s.id == parseInt(sectionId, 10));
      if (byId >= 0) return byId;
    }
    const url = (this.router.url || '').replace(/^\//, '');
    if (!url) return -1;
    return navigable.findIndex(s => s.location && (s.location.replace(/^\//, '') === url || s.location === url));
  }

  goToNextSection(): void {
    const navigable = (this.chapterSections || []).filter(s => Number(s.parentId));
    if (navigable.length === 0) return;
    if (!this.currentCourse) return;
    const idx = this.getCurrentNavigableIndex(navigable);
    const nextIdx = idx < 0 ? 0 : idx + 1;
    if (nextIdx >= navigable.length) return;
    const section = navigable[nextIdx];
    if (section.location && !section.content) {
      this.router.navigate([section.location.startsWith('/') ? section.location : '/' + section.location]);
    } else {
      this.router.navigate([this.getSectionLink(section)]);
    }
  }

  goToPreviousSection(): void {
    const navigable = (this.chapterSections || []).filter(s => Number(s.parentId));
    if (navigable.length === 0) return;
    if (!this.currentCourse) return;
    const idx = this.getCurrentNavigableIndex(navigable);
    if (idx <= 0) return;
    const section = navigable[idx - 1];
    if (section.location && !section.content) {
      this.router.navigate([section.location.startsWith('/') ? section.location : '/' + section.location]);
    } else {
      this.router.navigate([this.getSectionLink(section)]);
    }
  }

}
